import type {
  DocsAccessDocument,
  DocsAccessSection,
  DocsModelCapability,
  DocsSurface,
  DocsVariantAxis,
} from '@nadoutong/contracts';
import { useMemo, useState } from 'react';
import { localizeApiError } from '../../api/errorMessages.ts';
import { useToast } from '../../components/Toast.tsx';
import { useLocale, useT, type Locale } from '../../i18n/index.ts';
import { PageHeader, StatusLamp, Tabs, TabsList, TabsTrigger } from '../../ui/index.ts';
import { DocsBlockView } from './blocks.tsx';
import { EndpointCards } from './EndpointCards.tsx';
import { docsCardGridClass, groupDocsBlockFlow } from './flow.ts';
import { docsLabel, docsResolvedLabel } from './labels.ts';
import type { DocsPlaceholderValues } from './placeholders.ts';
import { DocsSection, tocChipClass, tocItemClass } from './primitives.tsx';
import { useActiveSection } from './useActiveSection.ts';
import { docsNodeIsVisible } from './visibility.ts';

const DOCS_SURFACE: DocsSurface = 'console_web';

export function ServedDocs({
  document,
  placeholders,
  availableCapabilities,
}: {
  readonly document: DocsAccessDocument;
  readonly placeholders: DocsPlaceholderValues;
  readonly availableCapabilities: ReadonlySet<DocsModelCapability> | null;
}): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const toast = useToast();
  const sections = useMemo(
    () =>
      document.sections.filter((section) =>
        docsNodeIsVisible(section, DOCS_SURFACE, availableCapabilities),
      ),
    [document.sections, availableCapabilities],
  );
  const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);
  const activeSection = useActiveSection(sectionIds);

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t('docs.toast.copied'));
    } catch (err) {
      toast.error(localizeApiError(err, t));
    }
  }

  const onCopy = (text: string): void => {
    void copy(text);
  };

  return (
    <section data-testid="docs-page" className="flex flex-col gap-4">
      <PageHeader title={t('docs.title')} sub={t('docs.subtitle')} testId="docs-header" />

      <EndpointCards />

      <nav
        aria-label={t('docs.toc.aria')}
        data-testid="docs-toc-chips"
        className="flex flex-wrap gap-1.5 xl:hidden"
      >
        {sections.map((section) => {
          const isActive = section.id === activeSection;
          return (
            <a
              key={section.id}
              href={`#${section.id}`}
              aria-current={isActive ? 'true' : undefined}
              data-testid={`docs-toc-chip-${section.id}`}
              className={tocChipClass(isActive)}
            >
              {docsNavLabel(section, locale, placeholders)}
            </a>
          );
        })}
      </nav>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[11rem_minmax(0,1fr)]">
        <aside className="hidden xl:block">
          <nav
            aria-label={t('docs.toc.aria')}
            data-testid="docs-toc"
            className="sticky top-4 flex flex-col gap-0.5 rounded-lg border border-line-soft bg-card p-1.5"
          >
            {sections.map((section) => {
              const isActive = section.id === activeSection;
              return (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  aria-current={isActive ? 'true' : undefined}
                  data-testid={`docs-toc-item-${section.id}`}
                  className={tocItemClass(isActive)}
                >
                  <span className="min-w-0 flex-1 truncate">
                    {docsNavLabel(section, locale, placeholders)}
                  </span>
                  {isActive ? <StatusLamp tone="rust" size="sm" /> : null}
                </a>
              );
            })}
          </nav>
        </aside>

        <div className="flex min-w-0 flex-col gap-4">
          {sections.map((section) => (
            <ServedSection
              key={section.id}
              section={section}
              placeholders={placeholders}
              availableCapabilities={availableCapabilities}
              onCopy={onCopy}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function ServedSection({
  section,
  placeholders,
  availableCapabilities,
  onCopy,
}: {
  readonly section: DocsAccessSection;
  readonly placeholders: DocsPlaceholderValues;
  readonly availableCapabilities: ReadonlySet<DocsModelCapability> | null;
  readonly onCopy: (text: string) => void;
}): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const [axisSelection, setAxisSelection] = useState<Readonly<Record<string, string>>>(() =>
    defaultAxisSelection(section.variant_axes),
  );

  const visibleBlocks = section.blocks.filter((block) =>
    docsNodeIsVisible(block, DOCS_SURFACE, availableCapabilities),
  );

  const copyLabel = t('docs.copy');

  return (
    <DocsSection id={section.id} title={docsResolvedLabel(section.title, locale, placeholders)}>
      {section.summary !== null ? (
        <p className="m-0 text-body leading-relaxed text-ink-mid">
          {docsResolvedLabel(section.summary, locale, placeholders)}
        </p>
      ) : null}
      {section.variant_axes.map((axis) => {
        const current = axisSelection[axis.key] ?? axis.values[0]?.key ?? '';
        return (
          <Tabs
            key={axis.key}
            value={current}
            onValueChange={(next) => {
              if (!axis.values.some((value) => value.key === next)) return;
              setAxisSelection((prev) => ({ ...prev, [axis.key]: next }));
            }}
          >
            <TabsList ariaLabel={docsLabel(axis.label, locale)}>
              {axis.values.map((value) => (
                <TabsTrigger
                  key={value.key}
                  value={value.key}
                  testId={`docs-axis-${section.id}-${axis.key}-${value.key}`}
                >
                  {docsLabel(value.label, locale)}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        );
      })}
      {groupDocsBlockFlow(visibleBlocks).map((item) => {
        if (item.kind === 'solo') {
          return (
            <DocsBlockView
              key={JSON.stringify(item.block)}
              block={item.block}
              ctx={{
                locale,
                values: placeholders,
                copyLabel,
                onCopy,
                sectionId: section.id,
                blockIndex: item.index,
                axisSelection,
              }}
            />
          );
        }
        const lead = item.items[0];
        if (lead === undefined) return null;
        return (
          <div
            key={item.items.map((entry) => JSON.stringify(entry.block)).join('|')}
            className={docsCardGridClass(item.group)}
            data-testid={`docs-card-grid-${section.id}-${String(lead.index)}`}
          >
            {item.items.map((entry) => (
              <DocsBlockView
                key={JSON.stringify(entry.block)}
                block={entry.block}
                ctx={{
                  locale,
                  values: placeholders,
                  copyLabel,
                  onCopy,
                  sectionId: section.id,
                  blockIndex: entry.index,
                  axisSelection,
                }}
              />
            ))}
          </div>
        );
      })}
    </DocsSection>
  );
}

/** Prefer nav_label; title only when the field is null/absent (#166 §5 transition fallback). */
function docsNavLabel(
  section: DocsAccessSection,
  locale: Locale,
  placeholders: DocsPlaceholderValues,
): string {
  return docsResolvedLabel(section.nav_label ?? section.title, locale, placeholders);
}

function defaultAxisSelection(axes: readonly DocsVariantAxis[]): Readonly<Record<string, string>> {
  const selection: Record<string, string> = {};
  for (const axis of axes) {
    const first = axis.values[0];
    if (first !== undefined) selection[axis.key] = first.key;
  }
  return selection;
}
